import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
    Bell, 
    BellRing,
    BellOff,
    Mail, 
    Clock, 
    DollarSign, 
    Tag, 
    MessageCircle, 
    Package, 
    Truck,
    ArrowLeft,
    Save,
    Loader2,
    Gavel,
    Sparkles,
    TrendingDown,
    Store,
    Search,
    Heart
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PushNotificationSettings from '@/components/notifications/PushNotificationSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick Push Actions Component
const QuickPushActions = () => {
    const { token } = useAuth();
    const [pushState, setPushState] = useState('loading'); // loading, unsupported, denied, inactive, active
    const [isToggling, setIsToggling] = useState(false);

    const checkPushState = useCallback(async () => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
            setPushState('unsupported');
            return;
        }

        if (Notification.permission === 'denied') {
            setPushState('denied');
            return;
        }

        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            
            if (subscription) {
                setPushState('active');
            } else {
                setPushState('inactive');
            }
        } catch {
            setPushState('inactive');
        }
    }, []);

    useEffect(() => {
        checkPushState();
    }, [checkPushState]);

    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const activatePush = async () => {
        setIsToggling(true);
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                setPushState('denied');
                toast.error('Ai blocat notificările. Activează-le din setările browserului.');
                return;
            }

            const vapidResponse = await fetch(`${API_URL}/api/notifications/vapid-public-key`);
            const { publicKey } = await vapidResponse.json();

            const registration = await navigator.serviceWorker.register('/sw.js');
            await navigator.serviceWorker.ready;

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey)
            });

            // Save to backend
            const p256dhKey = subscription.getKey('p256dh');
            const authKey = subscription.getKey('auth');

            await fetch(`${API_URL}/api/notifications/subscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    endpoint: subscription.endpoint,
                    keys: {
                        p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(p256dhKey))),
                        auth: btoa(String.fromCharCode.apply(null, new Uint8Array(authKey)))
                    }
                })
            });

            setPushState('active');
            toast.success('Notificările push au fost activate!');
        } catch (err) {
            console.error('Error activating push:', err);
            toast.error('Eroare la activarea notificărilor');
        } finally {
            setIsToggling(false);
        }
    };

    const deactivatePush = async () => {
        setIsToggling(true);
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (subscription) {
                await subscription.unsubscribe();
                
                await fetch(`${API_URL}/api/notifications/subscribe`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ endpoint: subscription.endpoint })
                });
            }

            setPushState('inactive');
            toast.success('Notificările push au fost dezactivate');
        } catch (err) {
            console.error('Error deactivating push:', err);
            toast.error('Eroare la dezactivarea notificărilor');
        } finally {
            setIsToggling(false);
        }
    };

    const testPush = async () => {
        try {
            const response = await fetch(`${API_URL}/api/notifications/test-push`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            
            if (response.ok && data.sent_count > 0) {
                toast.success(`Notificare de test trimisă!`);
            } else {
                toast.error('Niciun dispozitiv înregistrat');
            }
        } catch {
            toast.error('Eroare la trimiterea notificării de test');
        }
    };

    if (pushState === 'loading') {
        return (
            <div className="flex items-center gap-2 mb-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Se verifică starea notificărilor...</span>
            </div>
        );
    }

    if (pushState === 'unsupported') {
        return (
            <div className="flex items-center gap-2 mb-4 text-orange-600">
                <BellOff className="w-4 h-4" />
                <span className="text-sm">Browserul tău nu suportă notificări push</span>
            </div>
        );
    }

    if (pushState === 'denied') {
        return (
            <div className="flex items-center gap-2 mb-4 text-red-600">
                <BellOff className="w-4 h-4" />
                <span className="text-sm">Notificările sunt blocate în browser</span>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-3 mb-4">
            <Button
                variant={pushState === 'active' ? 'destructive' : 'default'}
                size="sm"
                onClick={pushState === 'active' ? deactivatePush : activatePush}
                disabled={isToggling}
            >
                {isToggling ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : pushState === 'active' ? (
                    <BellOff className="w-4 h-4 mr-2" />
                ) : (
                    <BellRing className="w-4 h-4 mr-2" />
                )}
                {pushState === 'active' ? 'Dezactivează push' : 'Activează push'}
            </Button>
            
            {pushState === 'active' && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={testPush}
                >
                    <Bell className="w-4 h-4 mr-2" />
                    Testează push
                </Button>
            )}
        </div>
    );
};

const NotificationSettingsPage = () => {
    const { token, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    
    const [preferences, setPreferences] = useState({
        push_enabled: true,
        email_enabled: true,
        auction_ending_alerts: true,
        auction_ending_times: [60, 30, 15, 5, 1],
        outbid_alerts: true,
        auction_won_alerts: true,
        min_auction_value: 0,
        max_auction_value: null,
        categories: [],
        new_message_alerts: true,
        email_on_new_message: true,
        order_status_alerts: true,
        shipping_alerts: true,
        // New granular settings
        new_listing_alerts: false,
        new_listing_instant: false,
        new_listing_digest: 'none',
        price_drop_alerts: true,
        price_drop_threshold: 10,
        email_on_price_drop: true,
        followed_seller_alerts: false,
        saved_search_alerts: false
    });

    const availableTimes = [
        { value: 60, label: '1 oră' },
        { value: 30, label: '30 min' },
        { value: 15, label: '15 min' },
        { value: 5, label: '5 min' },
        { value: 1, label: '1 min' }
    ];

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchPreferences();
        fetchCategories();
    }, [isAuthenticated, navigate]);

    const fetchPreferences = async () => {
        try {
            const response = await fetch(`${API_URL}/api/notifications/preferences`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPreferences(prev => ({ ...prev, ...data }));
            }
        } catch (error) {
            console.error('Error fetching preferences:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_URL}/api/notifications/categories-list`);
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch(`${API_URL}/api/notifications/preferences`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(preferences)
            });
            
            if (response.ok) {
                toast.success('Preferințele au fost salvate!');
            } else {
                toast.error('Eroare la salvarea preferințelor');
            }
        } catch (error) {
            toast.error('Eroare la salvarea preferințelor');
        } finally {
            setSaving(false);
        }
    };

    const toggleTime = (time) => {
        setPreferences(prev => {
            const times = prev.auction_ending_times || [];
            if (times.includes(time)) {
                return { ...prev, auction_ending_times: times.filter(t => t !== time) };
            } else {
                return { ...prev, auction_ending_times: [...times, time].sort((a, b) => b - a) };
            }
        });
    };

    const toggleCategory = (categoryId) => {
        setPreferences(prev => {
            const cats = prev.categories || [];
            if (cats.includes(categoryId)) {
                return { ...prev, categories: cats.filter(c => c !== categoryId) };
            } else {
                return { ...prev, categories: [...cats, categoryId] };
            }
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8" data-testid="notification-settings-page">
            <div className="max-w-3xl mx-auto px-4">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(-1)}
                        className="rounded-full"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Setări Notificări</h1>
                        <p className="text-gray-500">Configurează cum și când vrei să primești notificări</p>
                    </div>
                </div>
                
                {/* Quick Push Actions */}
                <QuickPushActions />

                <div className="space-y-6">
                    {/* Push Notifications Card */}
                    <PushNotificationSettings />

                    {/* General Settings */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Bell className="w-5 h-5" />
                                Canale de Notificare
                            </CardTitle>
                            <CardDescription>
                                Alege prin ce canale vrei să primești notificări
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Bell className="w-5 h-5 text-primary" />
                                    <div>
                                        <Label className="font-medium">Notificări Push</Label>
                                        <p className="text-sm text-muted-foreground">Notificări în browser în timp real</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={preferences.push_enabled}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, push_enabled: checked }))}
                                />
                            </div>
                            
                            <Separator />
                            
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Mail className="w-5 h-5 text-primary" />
                                    <div>
                                        <Label className="font-medium">Notificări Email</Label>
                                        <p className="text-sm text-muted-foreground">Primește emailuri pentru evenimente importante</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={preferences.email_enabled}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, email_enabled: checked }))}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Auction Alerts */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Gavel className="w-5 h-5" />
                                Alerte Licitații
                            </CardTitle>
                            <CardDescription>
                                Configurează alertele pentru licitațiile la care participi
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="font-medium">Licitații care expiră</Label>
                                    <p className="text-sm text-muted-foreground">Primește alerte înainte de terminarea licitațiilor</p>
                                </div>
                                <Switch
                                    checked={preferences.auction_ending_alerts}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, auction_ending_alerts: checked }))}
                                />
                            </div>

                            {preferences.auction_ending_alerts && (
                                <div className="pl-4 border-l-2 border-primary/20">
                                    <Label className="text-sm text-muted-foreground mb-3 block">
                                        <Clock className="w-4 h-4 inline mr-2" />
                                        Cu cât timp înainte să fii notificat?
                                    </Label>
                                    <div className="flex flex-wrap gap-2">
                                        {availableTimes.map(time => (
                                            <Button
                                                key={time.value}
                                                variant={preferences.auction_ending_times?.includes(time.value) ? "default" : "outline-solid"}
                                                size="sm"
                                                onClick={() => toggleTime(time.value)}
                                                className={preferences.auction_ending_times?.includes(time.value) 
                                                    ? "bg-primary hover:bg-primary/90" 
                                                    : ""}
                                            >
                                                {time.label}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <Separator />

                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="font-medium">Alerte "Ai fost depășit"</Label>
                                    <p className="text-sm text-muted-foreground">Când cineva licitează mai mult decât tine</p>
                                </div>
                                <Switch
                                    checked={preferences.outbid_alerts}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, outbid_alerts: checked }))}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="font-medium">Alerte "Ai câștigat"</Label>
                                    <p className="text-sm text-muted-foreground">Când câștigi o licitație</p>
                                </div>
                                <Switch
                                    checked={preferences.auction_won_alerts}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, auction_won_alerts: checked }))}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Value Filters */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <DollarSign className="w-5 h-5" />
                                Filtre Valoare
                            </CardTitle>
                            <CardDescription>
                                Primește notificări doar pentru licitații într-un anumit interval de preț
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="min-value">Valoare minimă (RON)</Label>
                                    <Input
                                        id="min-value"
                                        type="number"
                                        min="0"
                                        value={preferences.min_auction_value || 0}
                                        onChange={(e) => setPreferences(prev => ({ 
                                            ...prev, 
                                            min_auction_value: parseFloat(e.target.value) || 0 
                                        }))}
                                        className="mt-1"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        0 = fără limită minimă
                                    </p>
                                </div>
                                <div>
                                    <Label htmlFor="max-value">Valoare maximă (RON)</Label>
                                    <Input
                                        id="max-value"
                                        type="number"
                                        min="0"
                                        value={preferences.max_auction_value || ''}
                                        placeholder="Fără limită"
                                        onChange={(e) => setPreferences(prev => ({ 
                                            ...prev, 
                                            max_auction_value: e.target.value ? parseFloat(e.target.value) : null 
                                        }))}
                                        className="mt-1"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Gol = fără limită maximă
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Category Filters */}
                    {categories.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Tag className="w-5 h-5" />
                                    Filtre Categorii
                                </CardTitle>
                                <CardDescription>
                                    Selectează categoriile pentru care vrei să primești notificări (toate dacă nu selectezi niciuna)
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {categories.map(cat => (
                                        <div key={cat.category_id} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={cat.category_id}
                                                checked={preferences.categories?.includes(cat.category_id)}
                                                onCheckedChange={() => toggleCategory(cat.category_id)}
                                            />
                                            <Label htmlFor={cat.category_id} className="text-sm cursor-pointer">
                                                {cat.name}
                                            </Label>
                                        </div>
                                    ))}
                                </div>
                                {preferences.categories?.length === 0 && (
                                    <p className="text-sm text-muted-foreground mt-3">
                                        Nicio categorie selectată = primești notificări pentru toate categoriile
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* NEW: Instant Alerts for New Listings */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5" />
                                Listări Noi
                            </CardTitle>
                            <CardDescription>
                                Fii primul care află când apar produse noi în categoriile tale preferate
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="font-medium">Alerte pentru listări noi</Label>
                                    <p className="text-sm text-muted-foreground">Primește notificări când apar produse noi în categoriile selectate</p>
                                </div>
                                <Switch
                                    checked={preferences.new_listing_alerts}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, new_listing_alerts: checked }))}
                                />
                            </div>

                            {preferences.new_listing_alerts && (
                                <div className="pl-4 border-l-2 border-primary/20 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Label className="font-medium flex items-center gap-2">
                                                <Bell className="w-4 h-4" />
                                                Alertă instantanee (Push)
                                            </Label>
                                            <p className="text-sm text-muted-foreground">Primește notificare push imediat ce apare un produs nou</p>
                                        </div>
                                        <Switch
                                            checked={preferences.new_listing_instant}
                                            onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, new_listing_instant: checked }))}
                                        />
                                    </div>

                                    <div>
                                        <Label className="font-medium flex items-center gap-2 mb-2">
                                            <Mail className="w-4 h-4" />
                                            Rezumat pe email
                                        </Label>
                                        <Select 
                                            value={preferences.new_listing_digest || 'none'} 
                                            onValueChange={(value) => setPreferences(prev => ({ ...prev, new_listing_digest: value }))}
                                        >
                                            <SelectTrigger className="w-48">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Dezactivat</SelectItem>
                                                <SelectItem value="instant">⚡ Instant</SelectItem>
                                                <SelectItem value="daily">📅 Zilnic</SelectItem>
                                                <SelectItem value="weekly">📆 Săptămânal</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {preferences.new_listing_digest === 'instant' 
                                                ? 'Primești email imediat ce apare o listare nouă în categoriile tale'
                                                : 'Primește un email cu toate listările noi din categoriile tale'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* NEW: Price Drop Alerts */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <TrendingDown className="w-5 h-5" />
                                Alerte Scădere Preț
                            </CardTitle>
                            <CardDescription>
                                Fii notificat când prețul scade la produsele din lista ta de favorite
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="font-medium">Alerte scădere preț</Label>
                                    <p className="text-sm text-muted-foreground">Când un produs favorit are o reducere de preț</p>
                                </div>
                                <Switch
                                    checked={preferences.price_drop_alerts}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, price_drop_alerts: checked }))}
                                />
                            </div>

                            {preferences.price_drop_alerts && (
                                <div className="pl-4 border-l-2 border-primary/20 space-y-4">
                                    <div>
                                        <Label className="text-sm text-muted-foreground mb-2 block">
                                            Notifică-mă doar când prețul scade cu minim:
                                        </Label>
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                {[5, 10, 15, 20, 25].map(pct => (
                                                    <Button
                                                        key={pct}
                                                        variant={preferences.price_drop_threshold === pct ? "default" : "outline-solid"}
                                                        size="sm"
                                                        onClick={() => setPreferences(prev => ({ ...prev, price_drop_threshold: pct }))}
                                                    >
                                                        {pct}%
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-2">
                                            Exemplu: cu {preferences.price_drop_threshold}% selectat, vei fi notificat când un produs de 100 RON scade la {100 - preferences.price_drop_threshold} RON sau mai puțin
                                        </p>
                                    </div>
                                    
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Label className="text-sm font-medium">Notificare pe email</Label>
                                            <p className="text-xs text-muted-foreground">Primește email când prețul scade</p>
                                        </div>
                                        <Switch
                                            checked={preferences.email_on_price_drop ?? true}
                                            onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, email_on_price_drop: checked }))}
                                        />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* NEW: Followed Sellers & Saved Searches */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Heart className="w-5 h-5" />
                                Urmăriri
                            </CardTitle>
                            <CardDescription>
                                Notificări pentru vânzătorii și căutările pe care le urmărești
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Store className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <Label className="font-medium">Vânzători urmăriți</Label>
                                        <p className="text-sm text-muted-foreground">Când un vânzător urmărit adaugă un produs nou</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={preferences.followed_seller_alerts}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, followed_seller_alerts: checked }))}
                                />
                            </div>

                            <Separator />

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Search className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <Label className="font-medium">Căutări salvate</Label>
                                        <p className="text-sm text-muted-foreground">Când apar rezultate noi pentru căutările tale salvate</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={preferences.saved_search_alerts}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, saved_search_alerts: checked }))}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Other Notifications */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <MessageCircle className="w-5 h-5" />
                                Alte Notificări
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <MessageCircle className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <Label className="font-medium">Mesaje noi (Push)</Label>
                                        <p className="text-sm text-muted-foreground">Notificare push când primești un mesaj nou</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={preferences.new_message_alerts}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, new_message_alerts: checked }))}
                                />
                            </div>

                            <div className="flex items-center justify-between pl-8 border-l-2 border-primary/20">
                                <div className="flex items-center gap-3">
                                    <Mail className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <Label className="font-medium">Mesaje noi pe Email</Label>
                                        <p className="text-sm text-muted-foreground">Primește email când cineva îți trimite un mesaj nou</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={preferences.email_on_new_message}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, email_on_new_message: checked }))}
                                    disabled={!preferences.email_enabled}
                                />
                            </div>

                            <Separator />

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Package className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <Label className="font-medium">Status comenzi</Label>
                                        <p className="text-sm text-muted-foreground">Actualizări despre comenzile tale</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={preferences.order_status_alerts}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, order_status_alerts: checked }))}
                                />
                            </div>

                            <Separator />

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Truck className="w-5 h-5 text-gray-400" />
                                    <div>
                                        <Label className="font-medium">Livrare</Label>
                                        <p className="text-sm text-muted-foreground">Actualizări despre livrări</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={preferences.shipping_alerts}
                                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, shipping_alerts: checked }))}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Save Button */}
                    <div className="flex justify-end gap-4 pb-8">
                        <Button variant="outline" onClick={() => navigate(-1)}>
                            Anulează
                        </Button>
                        <Button 
                            onClick={handleSave} 
                            disabled={saving}
                            className="bg-primary hover:bg-primary/90"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Se salvează...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Salvează preferințele
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationSettingsPage;
